import React, { useState } from "react";
import ReactChipInput from "../../../../../../../CommonComponents/ReactChipInput/index.js";
import ModernOptionCard from "../../ModernOptionCard/ModernOptionCard.jsx";
import { ChantButton } from "../../../../../../../CommonComponents/index.js";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../../../../../services/firebaseService/connection.js";
import { toast } from "react-toastify";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../../../store/adminData/adminData";
import logger from "../../../../../../../utils/logger.js";

const Option1 = () => {
  const [chips, setChips] = useState([]);
  const [addEmailsLoading, setAddEmailsLoading] = useState(false);
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const verifyTwoWayGroupLink = async (
    linkedGroupId,
    currentGroupId,
    isLinkedGroup,
  ) => {
    if (isLinkedGroup) {
      const linkedGroup = await getDoc(doc(db, "group", linkedGroupId));
      if (linkedGroup.exists()) {
        const isValid = linkedGroup
          .data()
          ?.linkedGroupId?.includes(currentGroupId);
        return {
          valid: isValid,
          linkedGroupName: linkedGroup.data()?.groupName,
        };
      }
    }
    return { valid: false, linkedGroupName: "" };
  };

  const addChip = (value) => {
    const chipsTemp = chips.slice();
    var re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    var validEmail = re.test(String(value).toLowerCase());
    if (validEmail) {
      chipsTemp.push(value.toLowerCase());
      setChips(chipsTemp);
    } else {
      alert("Invalid Email");
    }
  };

  const removeChip = (index) => {
    const chipsTemp = chips.slice();
    chipsTemp.splice(index, 1);
    setChips(chipsTemp);
  };
  const addEmailsToFirebase = async (groupId, isLinkedGroup) => {
    if (!chips || chips.length === 0) {
      toast.error("Please add at least one email address");
      return;
    }

    if (!groupId) {
      toast.error("Group information not available");
      return;
    }

    try {
      setAddEmailsLoading(true);

      // Fetch all existing emails from userDetails subcollection
      const userDetailsRef = collection(
        db,
        "invite_emails",
        groupId,
        "userDetails",
      );
      const userDetailsSnapshot = await getDocs(userDetailsRef);
      const existingUserEmails = new Set();
      userDetailsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.email) {
          existingUserEmails.add(data.email.toLowerCase());
        }
      });

      // Fetch existing emails from invite_emails collection
      const emailListRef = doc(db, "invite_emails", groupId);
      const emailListDoc = await getDoc(emailListRef);
      const existingInviteEmails = new Set();
      if (emailListDoc.exists()) {
        const emails = emailListDoc.data().emails || [];
        emails.forEach((email) =>
          existingInviteEmails.add(email.toLowerCase()),
        );
      }

      // Filter out emails that already exist in either collection
      const emailsToAdd = [];
      const duplicateEmails = [];

      chips.forEach((email) => {
        const emailLower = email.toLowerCase();
        if (
          existingUserEmails.has(emailLower) ||
          existingInviteEmails.has(emailLower)
        ) {
          duplicateEmails.push(email);
        } else {
          emailsToAdd.push(email);
        }
      });

      let addedCount = 0;

      if (emailsToAdd.length > 0) {
        // Add unique emails to invite_emails collection
        const currentInviteEmails = emailListDoc.exists()
          ? emailListDoc.data().emails || []
          : [];
        const updatedInviteEmails = [...currentInviteEmails, ...emailsToAdd];

        if (emailListDoc.exists()) {
          await updateDoc(emailListRef, {
            emails: updatedInviteEmails,
          });
        } else {
          await setDoc(emailListRef, {
            emails: updatedInviteEmails,
          });
        }

        // Create userDetails docs for new emails
        const timestamp = Date.now();
        const userDetailsPromises = emailsToAdd.map((email) => {
          const userDocRef = doc(
            collection(db, "invite_emails", groupId, "userDetails"),
          );
          return setDoc(userDocRef, {
            email: email,
            invitedOn: timestamp,
          });
        });

        await Promise.all(userDetailsPromises);
        addedCount = emailsToAdd.length;
      }

      // Remove successfully added emails from chips, keep duplicates
      setChips(duplicateEmails);

      // Get linked group info if available
      let linkedGroupInfo = { valid: false, linkedGroupName: "" };
      if (
        !isLinkedGroup &&
        groupData?.linkedGroupId &&
        groupData?.linkedGroupId?.length > 0 &&
        groupId
      ) {
        linkedGroupInfo = await verifyTwoWayGroupLink(
          groupData?.linkedGroupId[0],
          groupId,
          true,
        );
        if (linkedGroupInfo.valid) {
          addEmailsToFirebase(
            groupData?.linkedGroupId[0],
            true,
            linkedGroupInfo.linkedGroupName,
          );
        }
      }

      // Show notifications only for primary group (not for linked group to avoid duplicate notifications)
      if (!isLinkedGroup) {
        const groupNames = linkedGroupInfo.valid
          ? `${groupData?.groupName} and ${linkedGroupInfo.linkedGroupName}`
          : groupData?.groupName;

        if (addedCount > 0 && duplicateEmails.length > 0) {
          toast.success(
            `${addedCount} email(s) added successfully to ${groupNames}\n${duplicateEmails.length} duplicate(s) skipped`,
          );
        } else if (addedCount > 0) {
          toast.success(
            `${addedCount} email(s) added successfully to ${groupNames}`,
          );
        } else {
          toast.warning(`All emails were already in ${groupNames}`);
        }
      }
    } catch (error) {
      logger.error("Error adding emails to Firebase:", error);
      toast.error("Failed to add emails. Please try again.");
    } finally {
      setAddEmailsLoading(false);
    }
  };

  return (
    <ModernOptionCard
      title="Option 1"
      subtitle="Add Individual"
      icon={<i className="fa fa-user-plus" />}
    >
      <div className="description-section">
        <p>
          To pre-register an individual or small number of members, type each
          email address below.
        </p>
        <p>
          Press &apos;Enter&apos; after each email address. When finished adding
          emails, click &apos;Upload&apos;.
        </p>
      </div>

      <div className="form-section">
        <div style={{ color: "#000000" }}>
          <ReactChipInput
            chips={chips}
            onSubmit={(value) => addChip(value)}
            onRemove={(index) => removeChip(index)}
            classes="custom-direction-column"
            validateEmail={true}
          />
        </div>
      </div>

      <div className="action-section">
        <ChantButton
          onClick={() => addEmailsToFirebase(groupData?.id, false)}
          loading={addEmailsLoading}
          disabled={chips.length === 0}
        >
          Upload
        </ChantButton>
      </div>
    </ModernOptionCard>
  );
};

export default Option1;
